import { useCallback, useEffect, useState } from "react";
import { useRouter } from "../../router";
import { advisoryApi, isUnavailable, type FlowProcess, type Pdf, type SessionState } from "./advisoryApi";
import { dateTime, statusLabel } from "./format";
import { OrderPipeline } from "./OrderPipeline";

// What the advisor does next in QP, by process state + flow flags (§11: this
// tenant's Investment Advice flow has compliance and external review, no signing).
function handoffCopy(p: FlowProcess | null): { title: string; body: string; button: string } | null {
  const flags = p?.flags ?? {};
  const signing = !!(flags.enableEidSigning || flags.enableDigitalSigning);
  switch (p?.status) {
    case "signing":
      return {
        title: "Waiting for the client's signature",
        body: "The client signs with BankID. Follow the signing, or resend the invitation, in Quantfolio.",
        button: "Continue in Quantfolio — signing ↗",
      };
    case "compliance_review":
      return {
        title: "With compliance for review",
        body: "A compliance officer approves or returns the advice in Quantfolio. This page picks up the decision by itself.",
        button: "Continue in Quantfolio — compliance review ↗",
      };
    case "external_review":
      return {
        title: "External review",
        body: "Compliance has approved. The external review is recorded from Andersen's order desk; the status updates here.",
        button: "Open the process in Quantfolio ↗",
      };
    case "submitted":
    case "pdf_generated":
      return {
        title: signing ? "Ready for signing" : "Ready for compliance review",
        body: signing
          ? "Generate the advice document, then send it to the client for signing in Quantfolio."
          : "Generate the advice document; the advice then moves to the compliance review.",
        button: signing ? "Continue in Quantfolio — signing ↗" : "Continue in Quantfolio — compliance review ↗",
      };
    default:
      return null;
  }
}

/**
 * After submit (§4.6): QP's process stepper, the advice documents (generate,
 * list, download with a fresh link) and the hand-off to Quantfolio for the one
 * step the API can't do. For this flow that step is the compliance review, not
 * signing. QP refuses to be framed, so the hand-off opens a new tab unless the
 * embed reports it can be framed.
 */
export function AfterSubmit({
  state,
  onRefresh,
  onSync,
  syncing,
  syncError,
}: {
  state: SessionState;
  onRefresh: () => void;
  /** Re-read QP now and drive the gate/inbox pipeline. */
  onSync: () => void;
  syncing: boolean;
  syncError: string | null;
}) {
  const { navigate } = useRouter();
  const sessionId = state.session.id;
  const process = state.process;
  const [pdfs, setPdfs] = useState<Pdf[] | null>(null);
  const [pdfErr, setPdfErr] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [handing, setHanding] = useState(false);
  const [handErr, setHandErr] = useState<string | null>(null);

  const loadPdfs = useCallback(() => {
    advisoryApi
      .pdfs(sessionId)
      .then((d) => {
        setPdfs(d.data);
        setPdfErr(null);
      })
      .catch((e) => setPdfErr(isUnavailable(e) ? "Documents can't be listed right now." : (e as Error).message));
  }, [sessionId]);
  useEffect(loadPdfs, [loadPdfs, process?.status]);

  async function generate() {
    setGenerating(true);
    setPdfErr(null);
    try {
      const r = await advisoryApi.generatePdfs(sessionId);
      if (Array.isArray(r.errors) && r.errors.length > 0) setPdfErr(`Some documents failed: ${r.errors.join(", ")}`);
      loadPdfs();
      onRefresh();
    } catch (e) {
      setPdfErr((e as Error).message);
    } finally {
      setGenerating(false);
    }
  }

  async function download(p: Pdf) {
    // Open the tab now (inside the click) so the browser doesn't block it,
    // then point it at a freshly signed link.
    const win = window.open("about:blank", "_blank");
    setDownloading(p.id);
    try {
      const { url } = await advisoryApi.pdfUrl(sessionId, p.id);
      if (win) {
        win.opener = null;
        win.location.href = url;
      } else window.location.href = url;
    } catch (e) {
      win?.close();
      setPdfErr((e as Error).message);
    } finally {
      setDownloading(null);
    }
  }

  async function handoff() {
    const win = window.open("about:blank", "_blank");
    setHanding(true);
    setHandErr(null);
    try {
      const r = await advisoryApi.handoff(sessionId);
      if (r.embed.embeddable === 1 && r.embed.id) {
        win?.close();
        navigate(`/app/embedded-flow?session=${r.embed.id}`);
        return;
      }
      const url = r.launchUrl ?? r.embed.embed_url;
      if (!url) throw new Error("Quantfolio did not return a link for this advice.");
      if (win) {
        win.opener = null;
        win.location.href = url;
      } else window.open(url, "_blank", "noopener");
      onRefresh();
    } catch (e) {
      win?.close();
      setHandErr(isUnavailable(e) ? "Quantfolio is not reachable right now. Try again in a moment." : (e as Error).message);
    } finally {
      setHanding(false);
    }
  }

  const steps = process?.steps ?? [];
  const current = process?.status ?? state.session.status;
  const currentIdx = steps.indexOf(current);
  const copy = handoffCopy(process);
  const hasPdf = (pdfs ?? []).some((p) => p.status === "completed");
  const needsPdf = pdfs !== null && !hasPdf && (current === "submitted" || current === "pdf_generated");
  const compliance = (process?.approvals ?? []).filter((a) => a.stepType === "compliance");

  return (
    <div className="adv-after">
      {steps.length > 0 && (
        <ol className="adv-stepper" aria-label="Advice process">
          {steps.map((s, i) => (
            <li
              key={s}
              className={
                current === "completed" || i < currentIdx ? "done" : i === currentIdx ? "current" : "upcoming"
              }
            >
              <span className="adv-stepper-dot">{current === "completed" || i < currentIdx ? "✓" : i + 1}</span>
              <span className="adv-stepper-label">{statusLabel(s)}</span>
            </li>
          ))}
        </ol>
      )}

      {current === "completed" ? (
        <div className="banner-success">
          <strong>The advice is complete.</strong> Its portfolio orders are pulled into the order inbox — see below.
        </div>
      ) : current === "cancelled" || current === "canceled" ? (
        <div className="banner-error">This advice was cancelled.</div>
      ) : (
        copy && (
          <section className="adv-handoff">
            <div>
              <span className="adv-label">Next step</span>
              <h3>{copy.title}</h3>
              <p className="muted">{copy.body}</p>
              {compliance.map((a) => (
                <p key={a.id} className="muted">
                  Compliance: <strong>{a.status}</strong>
                  {a.decidedByName ? ` by ${a.decidedByName}` : ""}
                  {a.decidedAt ? ` · ${dateTime(a.decidedAt)}` : ""}
                  {a.comment ? ` — “${a.comment}”` : ""}
                </p>
              ))}
              {process?.externalStatus && (
                <p className="muted">
                  External review: <strong>{process.externalStatus}</strong>
                </p>
              )}
              {handErr && <div className="banner-error">{handErr}</div>}
            </div>
            <button
              type="button"
              className={needsPdf ? "btn btn-ghost adv-btn" : "btn btn-primary adv-btn"}
              onClick={handoff}
              disabled={handing}
            >
              {handing ? "Opening…" : copy.button}
            </button>
          </section>
        )
      )}

      <OrderPipeline
        process={process}
        sessionStatus={state.session.status}
        pipeline={state.pipeline}
        onHandoff={handoff}
        handing={handing}
        onSync={onSync}
        syncing={syncing}
        syncError={syncError}
      />

      <section className="adv-subsection">
        <span className="adv-label">Advice documents</span>
        {pdfErr && <div className="banner-error">{pdfErr}</div>}
        {pdfs === null && !pdfErr ? (
          <p className="muted">Loading documents…</p>
        ) : pdfs && pdfs.length > 0 ? (
          <table className="table adv-table-compact">
            <thead>
              <tr>
                <th>Document</th>
                <th>Generated</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pdfs.map((p) => (
                <tr key={p.id}>
                  <td className="strong">{p.templateName}</td>
                  <td>{dateTime(p.generatedAt)}</td>
                  <td>{p.status === "completed" ? "Ready" : p.status}</td>
                  <td>
                    <div className="cell-actions">{p.status === "completed" && (
                      <button type="button" className="link-action" onClick={() => download(p)} disabled={downloading === p.id}>
                        {downloading === p.id ? "Opening…" : "Download PDF"}
                      </button>
                    )}
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted">No advice document yet.</p>
        )}
        {needsPdf && (
          <div className="adv-savebar">
            <span className="muted">The suitability report is generated from the submitted advice.</span>
            <button type="button" className="btn btn-primary adv-btn" onClick={generate} disabled={generating}>
              {generating ? "Generating…" : "Generate advice document"}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
